import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { colors } from '@config/colors';
import { spacing, borderRadius } from '@config/spacing';
import { typography } from '@config/typography';
import { media } from '@config/breakpoints';

export const PageRoot = styled.div`
  position: relative;
  min-height: 100vh;
  isolation: isolate;
`;

// ============================================================================
// Nav — floating pill bar (inspiration_menu reference)
// ============================================================================

export const NavOuter = styled.div`
  position: sticky;
  top: ${spacing.md};
  z-index: 50;
  display: flex;
  justify-content: center;
  padding: 0 ${spacing.md};

  ${media.md} {
    padding: 0 ${spacing.xl};
  }
`;

/* Page content maxes at 1280px (BentoContainer); the nav extends a bit
   beyond that — generous enough to feel like a primary surface on wide
   screens, capped so it doesn't run unbounded on huge monitors.
   $scrolled toggles once the hero is past, opaquing the background so
   content reads cleanly under the nav over non-seafoam sections. */
export const NavInner = styled.nav<{ $scrolled?: boolean }>`
  width: 100%;
  max-width: 800px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.lg};
  padding: ${spacing.xs} ${spacing.md};
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.45)'};
  backdrop-filter: blur(32px);
  -webkit-backdrop-filter: blur(32px);
  border-radius: ${borderRadius['3xl']};
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.08);
  transition: background 0.25s ease;

  ${media.md} {
    padding: ${spacing.sm} ${spacing.lg};
  }
`;

export const NavBrand = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-weight: 300;
  font-size: ${typography.fontSize.lg};
  color: ${colors.neutral.extraDark};
  text-decoration: none;
  letter-spacing: -0.01em;
  flex-shrink: 0;
  white-space: nowrap;
`;

/* Brand wordmark — hidden on small phones where the nav doesn't have room
   for icon + wordmark + two store CTAs without wrapping. The icon alone
   still anchors the brand. */
export const NavBrandText = styled.span`
  display: none;

  ${media.sm} {
    display: inline;
  }
`;

export const NavBrandMark = styled.img`
  display: block;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  object-fit: contain;
`;

export const NavCtas = styled.div`
  display: flex;
  gap: ${spacing.sm};
`;

export const StoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.sm};
  font-weight: 500;
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: ${borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.65);
  color: ${colors.highlight.extraDark};
  cursor: pointer;
  transition: transform 0.15s ease, background 0.15s ease;

  svg { width: 16px; height: 16px; }

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.95);
  }
`;

// ============================================================================
// Hero — one centred column: the name, what it is, and where to get it.
// ============================================================================

/* Relative, because the hero's arriving light (`hero-intro.tsx`) is laid
   over this box rather than in its flow — so it blooms where the words
   are about to be, and leaves no gap once it is gone. */
export const HeroLayout = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

export const HeroCopy = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

/* The site's original headline type — Poppins Light, slate — on one line
   at every width: "The Home CEO" is the name, not a sentence to wrap. */
export const HeroHeadline = styled.h1`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(2.25rem, 5vw, 5rem);
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: -0.03em;
  white-space: nowrap;
  color: ${colors.highlight.primary};
  margin: 0 0 ${spacing.lg};
`;

export const HeroSubheadline = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: clamp(1.25rem, 2.4vw, 2rem);
  font-weight: 300;
  line-height: 1.35;
  color: ${colors.highlight.dark};
  margin: 0 0 ${spacing['4xl']};
  /* Two even lines where it wraps (a phone), not a lone "household". */
  text-wrap: balance;
`;

export const HeroCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.md};
  justify-content: center;
`;

// ============================================================================
// Testimonials (Section 6)
// ============================================================================

export const TestimonialMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const TestimonialName = styled.div`
  font-family: ${typography.fontFamily.small};
  font-weight: 500;
  color: ${colors.neutral.extraDark};
  font-size: ${typography.fontSize.base};
`;

export const TestimonialRole = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.sm};
  font-weight: 400;
  color: ${colors.highlight.dark};
`;

// ============================================================================
// Stats (Section 6)
// ============================================================================

// ============================================================================
// Wall of Love — varied-shape testimonial bento
// ============================================================================

export const LoveWall = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: ${spacing.lg};
  position: relative;

  ${media.md} {
    gap: ${spacing.xl};
  }
`;

export const LoveDecorationQuote = styled.div`
  position: absolute;
  top: -${spacing.xl};
  right: 0;
  font-family: 'Poppins', serif;
  font-size: 8rem;
  font-weight: 700;
  line-height: 1;
  color: rgba(255, 255, 255, 0.35);
  pointer-events: none;
  user-select: none;
  z-index: 0;
`;

export const LoveCard = styled.article<{
  $cols?: number;
  $rows?: number;
  $colsMd?: number;
  $rowsMd?: number;
  $tone?: 'solid' | 'glass' | 'accent';
  $align?: 'left' | 'center';
}>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  background: ${({ $tone }) => {
    switch ($tone) {
      case 'accent':
        return 'linear-gradient(160deg, rgba(216,243,237,0.7) 0%, rgba(255,255,255,0.5) 100%)';
      case 'glass':
        return 'rgba(255, 255, 255, 0.55)';
      case 'solid':
      default:
        return 'rgba(255, 255, 255, 0.92)';
    }
  }};
  backdrop-filter: blur(28px);
  -webkit-backdrop-filter: blur(28px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 28px;
  padding: ${spacing.xl};
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.08);
  text-align: ${({ $align }) => $align ?? 'left'};

  grid-column: span 12;
  grid-row: span ${({ $rows }) => $rows ?? 1};

  ${media.md} {
    grid-column: span ${({ $colsMd, $cols }) => $colsMd ?? $cols ?? 6};
    grid-row: span ${({ $rowsMd, $rows }) => $rowsMd ?? $rows ?? 1};
    padding: ${spacing['2xl']};
  }

  ${media.lg} {
    grid-column: span ${({ $cols }) => $cols ?? 4};
    grid-row: span ${({ $rows }) => $rows ?? 1};
  }
`;

export const LoveBigQuote = styled.blockquote`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(1.25rem, 1.8vw, 1.625rem);
  font-weight: 300;
  line-height: 1.45;
  letter-spacing: -0.01em;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.xl};
  font-style: italic;
  flex: 1;
`;

export const LoveSmallQuote = styled.blockquote`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.base};
  font-weight: 300;
  line-height: 1.55;
  color: ${colors.neutral.extraDark};
  margin: 0 0 ${spacing.lg};
  font-style: italic;
  flex: 1;
`;

export const LovePullQuote = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(1.5rem, 2.4vw, 2rem);
  font-weight: 300;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: ${colors.accent.coral.dark};
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
`;

export const LoveAvatarRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  flex: 1;
`;

export const LoveAvatarStack = styled.div`
  display: flex;
  & > span {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px solid white;
    background: linear-gradient(
      160deg,
      ${colors.brand.primaryDark} 0%,
      ${colors.accent.coral.light} 100%
    );
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-family: 'Poppins', ${typography.fontFamily.heading};
    font-weight: 500;
    font-size: ${typography.fontSize.sm};
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.1);

    & + span { margin-left: -12px; }
  }
`;

export const LoveAvatarCopy = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: 400;
  color: ${colors.text};
  line-height: 1.4;

  strong {
    display: block;
    font-weight: 500;
    color: ${colors.neutral.extraDark};
    margin-bottom: 2px;
  }
`;

// ============================================================================
// Blog (Section 8) — graphical cards with thumbnails
// ============================================================================

/* Centred on the page, so two posts read as a pair rather than as the
   start of a row of three. */
export const BlogHead = styled.div`
  max-width: 880px;
  margin: 0 auto;
  text-align: center;
`;

export const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.lg};
  max-width: 880px;
  margin: 0 auto;

  ${media.md} {
    grid-template-columns: repeat(2, 1fr);
    gap: ${spacing.xl};
  }
`;

export const BlogLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
  height: 100%;
`;

export const BlogCardInner = styled.article<{ $tone?: 'image' | 'highlight' }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ $tone }) =>
    $tone === 'highlight'
      ? `linear-gradient(160deg, ${colors.brand.exxtraDark} 0%, ${colors.brand.exxxxtraDark} 100%)`
      : 'rgba(255, 255, 255, 0.92)'};
  color: ${({ $tone }) => ($tone === 'highlight' ? 'white' : colors.text)};
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 26px 60px rgba(15, 23, 42, 0.14);
  }
`;

export const BlogThumb = styled.div<{ $url?: string }>`
  width: 100%;
  height: 200px;
  background-color: ${colors.brand.primaryLight};
  background-image: ${({ $url }) => ($url ? `url('${$url}')` : 'none')};
  background-size: cover;
  background-position: center;
`;

export const BlogBody = styled.div`
  padding: ${spacing.xl};
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${spacing.sm};
`;

export const BlogEyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.7;
`;

export const BlogTitle = styled.h3`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: ${typography.fontSize.xl};
  font-weight: 300;
  letter-spacing: -0.01em;
  line-height: 1.25;
  margin: 0;
`;

export const BlogBlurb = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: 300;
  line-height: 1.55;
  margin: 0;
  flex: 1;
`;

export const BlogMeta = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  opacity: 0.65;
  margin-top: ${spacing.md};
`;

export const SeeAllLink = styled(Link)`
  display: block;
  width: fit-content;
  margin: ${spacing['2xl']} auto 0;
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.base};
  font-weight: 500;
  color: ${colors.brand.exxxtraDark};
  text-decoration: none;

  &:hover { color: ${colors.brand.exxxxtraDark}; text-decoration: underline; }
`;

// ============================================================================
// Footer (Section 9) — floating panel + watermark + CTA
// ============================================================================

export const FooterShell = styled.footer`
  position: relative;
  padding: ${spacing['3xl']} ${spacing.lg} ${spacing['2xl']};

  ${media.md} {
    padding: ${spacing['4xl']} ${spacing.xl} ${spacing['3xl']};
  }
`;

export const FooterPanel = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(32px);
  -webkit-backdrop-filter: blur(32px);
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 32px;
  padding: ${spacing['2xl']};
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.1);

  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing['2xl']};

  ${media.md} {
    grid-template-columns: 2fr 1fr 1fr 1fr;
    gap: ${spacing['3xl']};
    padding: ${spacing['3xl']};
  }
`;

export const FooterBrandCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const FooterBrandLogo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-weight: 300;
  font-size: ${typography.fontSize.lg};
  color: ${colors.neutral.extraDark};

  img {
    width: 36px;
    height: 36px;
  }
`;

export const FooterBrandDesc = styled.p`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${typography.fontSize.sm};
  font-weight: 300;
  line-height: 1.55;
  color: ${colors.text};
  margin: 0;
  max-width: 32ch;
`;

export const FooterCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

export const FooterColTitle = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${colors.highlight.dark};
  margin-bottom: ${spacing.xs};
`;

export const FooterColLink = styled(Link)`
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.sm};
  font-weight: 400;
  color: ${colors.highlight.extraDark};
  text-decoration: none;

  &:hover { color: ${colors.accent.coral.dark}; }
`;

export const FooterWatermark = styled.div`
  text-align: center;
  margin-top: ${spacing['4xl']};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xl};
  position: relative;
`;

export const FooterWatermarkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md};
  pointer-events: none;
  user-select: none;
  opacity: 0.45;

  img {
    width: clamp(80px, 12vw, 160px);
    height: clamp(80px, 12vw, 160px);
  }
`;

export const FooterWatermarkText = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: clamp(3rem, 9vw, 7rem);
  font-weight: 300;
  letter-spacing: -0.04em;
  line-height: 0.9;
  color: ${colors.highlight.extraDark};
`;

export const FooterCta = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.sm};
  font-family: ${typography.fontFamily.CTA};
  font-size: ${typography.fontSize.base};
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: ${spacing.lg} ${spacing['2xl']};
  border-radius: ${borderRadius.full};
  background: linear-gradient(
    135deg,
    ${colors.accent.coral.dark} 0%,
    ${colors.accent.coral.extraDark} 100%
  );
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 18px 44px rgba(239, 68, 68, 0.32);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 24px 56px rgba(239, 68, 68, 0.4);
  }

  svg { width: 18px; height: 18px; }
`;

export const FooterCtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: ${spacing.md};
`;

export const FooterSocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  color: ${colors.accent.coral.extraDark};
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover { color: ${colors.accent.coral.exxtraDark}; }
`;

/* Sits alongside the LinkedIn logo in the footer social row. Triggers
   the Newsletter overlay — kept as a text button (semantics: modal
   trigger, not navigation) with the coral palette so it reads as one
   family with the LinkedIn icon and the Newsletter overlay itself. */
export const FooterFollowButton = styled.button`
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.sm};
  font-weight: 500;
  color: ${colors.accent.coral.extraDark};
  transition: color 0.15s ease;

  &:hover { color: ${colors.accent.coral.exxtraDark}; }
`;

export const FooterCopyright = styled.div`
  text-align: center;
  margin-top: ${spacing['2xl']};
  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.sm};
  font-weight: 400;
  color: ${colors.highlight.dark};
`;

